import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApiService } from '@/services/api/client.service';
import { apiClient } from '@/services/api';
import type { Lead, Conversation, ConversationPaginationResponse } from '@/types/api.types';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Mail, Phone, User, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface AdminClientLeadsTableProps {
  clientId: string;
}

export function AdminClientLeadsTable({ clientId }: AdminClientLeadsTableProps) {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-leads', clientId, page],
    queryFn: () => clientApiService.getClientLeads(clientId, { page, limit }),
    staleTime: 60 * 1000, // 1 minute
  });

  // Fetch conversations for selected lead
  const { data: conversationsData, isLoading: conversationsLoading } = useQuery<ConversationPaginationResponse | null>({
    queryKey: ['lead-conversations', selectedLead?.id],
    queryFn: async () => {
      if (!selectedLead) return null;
      const response = await apiClient.get<{ success: boolean; data: ConversationPaginationResponse; requestId: string }>(
        `/leads/${selectedLead.id}/conversations`
      );
      return response.data.data;
    },
    enabled: !!selectedLead && showLeadDetail,
    retry: false,
  });

  const leads = data?.data || [];
  const totalPages = data?.total_pages || 1;

  const getStatusColor = (status: string | null) => {
    const colors: Record<string, string> = {
      'Caliente': 'text-red-600 bg-red-50 border-red-200',
      'Tibio': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Frio': 'text-blue-600 bg-blue-50 border-blue-200',
      'Por calificar': 'text-gray-600 bg-gray-50 border-gray-200'
    };
    return status ? colors[status] || colors['Por calificar'] : colors['Por calificar'];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  const getLeadDisplayName = (lead: Lead): string => {
    if (lead.full_name) return lead.full_name;
    if (lead.email) return lead.email;
    return `Cliente ${lead.phone?.slice(-4) || 'desconocido'}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar leads: {(error as Error).message}
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay leads para este cliente
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Leads List */}
      <div className="space-y-2">
        {leads.map((lead) => (
          <div
            key={lead.id}
            className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">
                    {lead.full_name || 'Sin nombre'}
                  </span>
                  {lead.lead_status && (
                    <Badge className={getStatusColor(lead.lead_status)}>
                      {lead.lead_status}
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  {lead.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {lead.email}
                    </div>
                  )}
                  {lead.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {lead.phone}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right text-sm">
                <div className="text-muted-foreground">
                  {lead.conversation_count} conversación{lead.conversation_count !== 1 ? 'es' : ''}
                </div>
                {lead.budget && (
                  <div className="font-medium text-green-600">{lead.budget}</div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
              <div className="flex items-center gap-4">
                <div>Creado: {formatDate(lead.created_at)}</div>
                <div>Último mensaje: {formatDate(lead.last_message_date)}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewLead(lead)}
                className="h-7 text-xs"
              >
                <Eye className="w-3 h-3 mr-1" />
                Ver detalle
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Página {page} de {totalPages} • {data?.total || 0} leads
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Lead Detail & Conversations Dialog */}
      <Dialog open={showLeadDetail} onOpenChange={setShowLeadDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {selectedLead ? getLeadDisplayName(selectedLead) : 'Detalle del Lead'}
            </DialogTitle>
            <DialogDescription>
              Información completa y conversaciones del lead
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="info" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="conversations">Conversaciones</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 max-h-[500px] overflow-y-auto">
              {selectedLead && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID Lead</p>
                    <p className="font-mono">#{selectedLead.original_lead_id || selectedLead.id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nombre</p>
                    <p>{selectedLead.full_name || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p>{selectedLead.email || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Teléfono</p>
                    <p>{selectedLead.phone || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Estado</p>
                    <Badge className={getStatusColor(selectedLead.lead_status)}>
                      {selectedLead.lead_status || 'Por calificar'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Presupuesto</p>
                    <p>{selectedLead.budget || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total conversaciones</p>
                    <p>{selectedLead.conversation_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Último mensaje</p>
                    <p>{formatDate(selectedLead.last_message_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Última interacción</p>
                    <p>{selectedLead.last_interaction ? formatDate(selectedLead.last_interaction) : 'Sin interacción'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Creado</p>
                    <p>{formatDate(selectedLead.created_at)}</p>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="conversations" className="h-[400px] overflow-y-auto">
              {conversationsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <LoadingSpinner />
                </div>
              ) : conversationsData && conversationsData.conversations && conversationsData.conversations.length > 0 ? (
                <div className="space-y-4">
                  {[...conversationsData.conversations]
                    .sort((a: Conversation, b: Conversation) =>
                      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                    )
                    .map((conv: Conversation) => (
                    <div
                      key={conv.id}
                      className={`p-3 rounded-lg ${
                        conv.sender_type === 'user'
                          ? 'bg-blue-50 ml-8'
                          : 'bg-gray-50 mr-8'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm font-medium">
                          {conv.sender_type === 'user' ? 'Cliente' : 'Bot'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(conv.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm">{conv.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No hay conversaciones disponibles
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
